import { useRef } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
} from "@chakra-ui/react";
import CDS from "design-system";
import { useSkipRoleForJourney } from "features/NewHireJourney/hooks";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  journeyId: string;
  /** Role key sent to the API (e.g. "buddy"). */
  roleKey: string;
  /** Human label for the role (e.g. "buddy"). */
  roleLabel: string;
  newHireName: string;
  /** Called after the skip succeeds — lets the caller hide the action item immediately. */
  onSkipped?: () => void;
}

export default function SkipRoleModal({
  isOpen, onClose, journeyId, roleKey, roleLabel, newHireName, onSkipped,
}: Props) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { mutate: skipRole, isPending } = useSkipRoleForJourney(journeyId);

  const handleConfirm = () => {
    skipRole(roleKey, {
      onSuccess: () => {
        onSkipped?.();
        onClose();
      },
    });
  };

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={handleClose} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="semibold">
            Skip {roleLabel} for {newHireName}&rsquo;s journey?
          </AlertDialogHeader>

          <AlertDialogBody>
            <Box mb={2}>
              <CDS.Text14 color="gray.600">
                Every open {roleLabel} action — the assignment task, plus any {roleLabel} emails,
                messages, and meetings — is marked skipped. Anything already sent or completed is left as-is.
              </CDS.Text14>
            </Box>
            <CDS.Text14 color="gray.600" fontStyle="italic">
              This can&rsquo;t be undone.
            </CDS.Text14>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Box flex={1} />
            <CDS.Button ref={cancelRef} variant={CDS.Button.variant.ghost} onClick={handleClose} disabled={isPending}>
              Cancel
            </CDS.Button>
            <Box ml={3}>
              <CDS.Button onClick={handleConfirm} loading={isPending}>
                Skip {roleLabel}
              </CDS.Button>
            </Box>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
